/**
 * Holiday list Excel export.
 *
 * Reuses the same API the holidays table reads (`holiday/get/all`) with the
 * page's current filters (year + calendar), pages through every match and
 * downloads a single-sheet workbook.
 *
 * Public API:
 *   exportHolidaysXlsx({ year, calenderId, onProgress, onError, onSuccess })
 */
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import { apiClient } from '@libs/apiRequest';

/** Holidays fetched per request (a year holds few dozen at most). */
const PAGE_SIZE = 200;
/** Hard stop for the fetch loop (safety against a runaway loop). */
const MAX_PAGES = 50;

const fetchAllHolidays = async ({ year, calenderId }, onProgress) => {
    const holidays = [];
    let total = null;
    for (let page = 0; page < MAX_PAGES; page += 1) {
        const { data } = await apiClient.get('holiday/get/all', {
            params: {
                limit: PAGE_SIZE,
                offset: page === 0 ? '' : String(page * PAGE_SIZE),
                ...(year ? { year } : {}),
                ...(calenderId ? { calenderId } : {})
            }
        });
        total = parseInt(data?.total) || 0;
        const batch = data?.data || [];
        holidays.push(...batch);
        onProgress && onProgress(Math.min(holidays.length, total), total);
        if (!batch.length || holidays.length >= total) break;
    }
    return holidays;
};

const COLUMNS = [
    { label: 'Name', value: (h) => h.name || '' },
    {
        label: 'Date',
        value: (h) => (h.date ? dayjs(h.date).format('YYYY-MM-DD') : '')
    },
    {
        label: 'Day',
        value: (h) => (h.date ? dayjs(h.date).format('dddd') : '')
    },
    { label: 'Description', value: (h) => h.description || '' },
    { label: 'Calendar', value: (h) => h.calender?.name || '' }
];

const buildSheet = (headers, rows) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((header, c) => {
        let wch = String(header).length;
        rows.forEach((row) => {
            const v = row[c];
            if (v !== undefined && v !== null)
                wch = Math.max(wch, String(v).length);
        });
        return { wch: Math.min(60, Math.max(10, wch + 2)) };
    });
    ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: rows.length, c: Math.max(0, headers.length - 1) }
        })
    };
    return ws;
};

export async function exportHolidaysXlsx({
    year,
    calenderId,
    onProgress,
    onError,
    onSuccess
} = {}) {
    const progress = (message) => onProgress && onProgress(message);

    try {
        progress('Fetching holidays…');
        const holidays = await fetchAllHolidays(
            { year, calenderId },
            (done, total) => progress(`Fetching holidays ${done}/${total}…`)
        );
        if (!holidays.length) {
            throw new Error('No holidays match the selected filters.');
        }

        holidays.sort(
            (a, b) => new Date(a.date || 0) - new Date(b.date || 0)
        );

        progress('Building Excel workbook…');
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(
                COLUMNS.map((column) => column.label),
                holidays.map((holiday) =>
                    COLUMNS.map((column) => column.value(holiday))
                )
            ),
            'Holidays'
        );

        const calendarName = calenderId
            ? holidays.find((h) => h.calender?.name)?.calender?.name
            : null;
        const nameParts = [
            'holidays',
            year,
            calendarName
                ? calendarName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                : null
        ].filter(Boolean);
        XLSX.writeFile(wb, `${nameParts.join('-')}.xlsx`);
        onSuccess && onSuccess();
    } catch (err) {
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to generate the holidays Excel file.';
        onError && onError(msg);
    }
}

export default exportHolidaysXlsx;
